import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Field, NSelect, Loading, Plate, useFetch } from "@/components/common";
import { PhotoSlot, PhotoPicker, useUploadsPending, useDraft } from "@/components/Photos";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fDate, todayIso } from "@/lib/api";

const ZONES = ["Face avant", "Face arrière", "Côté gauche", "Côté droit", "Pare-brise", "Toit", "Intérieur / cabine", "Jantes / roues", "Autre"];

function DamageEditor({ d, onChange, onRemove, i }) {
  const set = (k) => (v) => {
    const val = v?.target ? v.target.value : v;
    onChange((y) => ({ ...y, [k]: val }));
  };
  return (
    <div className="border rounded-xl p-3 space-y-3 bg-white" data-testid={`edl-damage-${i}`}>
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="Zone"><NSelect value={d.zone} onChange={set("zone")} placeholder="Choisir…" options={ZONES.map((z) => ({ value: z, label: z }))} testid={`edl-damage-${i}-zone`} /></Field>
        <Field label="Date du constat"><Input type="date" value={d.date} onChange={set("date")} className="h-10 bg-white" data-testid={`edl-damage-${i}-date`} /></Field>
        <div className="flex items-end justify-end"><button type="button" onClick={onRemove} className="text-rose-600 text-xs font-bold inline-flex items-center gap-1" data-testid={`edl-damage-${i}-remove`}><Trash2 className="w-4 h-4" /> Retirer</button></div>
      </div>
      <Field label="Courte description"><Input value={d.description} onChange={set("description")} className="bg-white" data-testid={`edl-damage-${i}-description`} /></Field>
      <PhotoPicker items={d.photos} onChange={(fn) => onChange((y) => ({ ...y, photos: fn(y.photos) }))} testid={`edl-damage-${i}-photos`} />
    </div>
  );
}

export default function EtatDesLieuxForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const dmg = useFetch(`/vehicles/${id}/damages`);
  const veh = useFetch(`/vehicles/${id}`);
  const [dr, setDr, clear] = useDraft(`draft_edl_${id}`, { photos: {}, damages: [], reason: "reparation", km: "", comment: "" });
  const { photos, damages, reason, km, comment } = dr;
  const up = (k) => (val) => setDr((s) => ({ ...s, [k]: typeof val === "function" ? val(s[k]) : val }));
  const setPhotos = up("photos");
  const setDamages = up("damages");
  const setReason = up("reason");
  const setKm = up("km");
  const setComment = up("comment");
  const [busy, setBusy] = useState(false);
  const uploading = useUploadsPending();
  if (dmg.loading || veh.loading || !dmg.data || !veh.data) return <Loading />;
  const first = !dmg.data.current_inspection;
  const slotChange = (k) => (fn) => setPhotos((p) => ({ ...p, [k]: fn(p[k] || []) }));
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/vehicles/${id}/inspections`, {
        reason: first ? "premier" : reason, mileage: km ? Number(km) : null, comment, photos,
        damages: damages.map((d) => ({ ...d, photos: d.photos.map((p) => p.file_id) })),
      });
      clear();
      toast.success("État des lieux enregistré");
      nav(`/livraison/vehicules/${id}?tab=edl`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="max-w-4xl" data-testid="edl-form-page">
      <Link to={`/livraison/vehicules/${id}`} className="inline-flex items-center gap-1 text-sm text-slate-500 mb-4" data-testid="edl-back"><ArrowLeft className="w-4 h-4" /> Fiche véhicule</Link>
      <PageHeader overline={`${first ? "Premier état des lieux" : "Nouvel état des lieux complet"} · ${fDate(todayIso())}`} title="État des lieux du véhicule">
        <Plate>{veh.data.plate}</Plate>
      </PageHeader>
      <div className="bg-white border rounded-xl p-4 grid sm:grid-cols-3 gap-3 mb-5">
        <Field label="Réalisé par"><div className="h-10 px-3 flex items-center rounded-md bg-slate-50 border text-sm font-semibold" data-testid="edl-author">{user.name}</div></Field>
        {!first && <Field label="Motif"><NSelect value={reason} onChange={setReason} testid="edl-reason-select" options={Object.entries(dmg.data.reasons).filter(([k]) => k !== "premier" && (k !== "admin" || user.role === "admin")).map(([k, l]) => ({ value: k, label: l }))} /></Field>}
        <Field label="Kilométrage"><Input type="number" inputMode="numeric" value={km} onChange={(e) => setKm(e.target.value)} className="h-10 bg-white" data-testid="edl-mileage-input" /></Field>
      </div>
      <div className="text-[11px] uppercase font-bold tracking-wider text-slate-500 mb-2">Photos — appuyez sur un cadre pour prendre la photo</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="edl-slots">
        {Object.entries(dmg.data.slots).map(([k, label]) => <PhotoSlot key={k} label={label} ids={photos[k] || []} onChange={slotChange(k)} testid={`edl-slot-${k}`} />)}
      </div>
      <div className="flex items-center justify-between mb-2">
        <div className="font-display font-bold text-[#0E2D43] tracking-wide">DOMMAGES DÉJÀ PRÉSENTS</div>
        <button type="button" onClick={() => setDamages((cur) => [...cur, { zone: "", description: "", date: todayIso(), photos: [] }])} className="inline-flex items-center gap-1 text-sm font-bold text-[#E0830B]" data-testid="edl-add-damage"><Plus className="w-4 h-4" /> Déclarer un dommage</button>
      </div>
      <div className="space-y-3 mb-5">
        {!damages.length && <div className="text-sm text-slate-400">Aucun dommage déclaré.</div>}
        {damages.map((d, i) => <DamageEditor key={i} i={i} d={d} onChange={(upd) => setDamages((prev) => prev.map((y, j) => (j === i ? upd(y) : y)))} onRemove={() => setDamages((prev) => prev.filter((_, j) => j !== i))} />)}
      </div>
      <Field label="Commentaire"><Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} className="bg-white" data-testid="edl-comment" /></Field>
      <p className="text-xs text-emerald-700 mt-3" data-testid="edl-draft-note">Chaque photo est sauvegardée automatiquement : si la page se recharge, vous reprenez où vous en étiez.</p>
      <p className="text-xs text-slate-500 my-3">L'état des lieux est daté et conservé définitivement ; il ne remplace pas les versions précédentes.</p>
      <button onClick={submit} disabled={busy || uploading > 0} className="w-full h-14 rounded-lg bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] font-bold flex items-center justify-center gap-2 sticky bottom-3 shadow-lg disabled:bg-slate-300 disabled:text-slate-600" data-testid="edl-submit">
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : uploading ? `Envoi des photos en cours (${uploading})…` : "Enregistrer l'état des lieux"}
      </button>
    </div>
  );
}
